import {Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress} from "@mui/material";
import {ReactNode} from "react";
import {colors, fonts, gradients, radii, spacing} from "../theme";
import {btnBreathe} from "./animations";

type ButtonVariant = "filled-gradient" | "filled" | "outline" | "ghost";
type ButtonSize = "small" | "medium" | "large";

type ButtonProps = Omit<MuiButtonProps, "variant" | "color" | "size"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
  fullWidth?: boolean;
  loading?: boolean;
  animateIcon?: boolean;
};

const sizeConfig = {
  small: {
    height: 32,
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: radii.pill,
    minWidth: 80,
  },
  medium: {
    height: 36,
    padding: `${spacing[3]}`,
    fontSize: "14px",
    borderRadius: radii.pill,
    minWidth: 99,
  },
  large: {
    height: 48,
    padding: `${spacing[3]} ${spacing[5]}`,
    fontSize: "16px",
    borderRadius: radii.pill,
    minWidth: 108,
  },
} as const;

const variantStyles = {
  "filled-gradient": {
    background: gradients.buttonFilled,
    color: colors.white,
    border: "none",
    "&:hover": {
      background: gradients.buttonFilledHover,
    },
    "&:active": {
      background: gradients.buttonFilledActive,
    },
    "&.Mui-disabled": {
      background: gradients.buttonFilled,
      color: colors.white,
      opacity: 0.4,
    },
  },
  filled: {
    backgroundColor: colors.primary[800],
    color: colors.white,
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: colors.primary[900],
    },
    "&:active": {
      backgroundColor: colors.primary[1000],
    },
    "&.Mui-disabled": {
      backgroundColor: colors.primary[800],
      color: colors.white,
      opacity: 0.4,
    },
  },
  outline: {
    backgroundColor: "transparent",
    color: colors.primary[700],
    border: `1px solid ${colors.primary[200]}`,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: colors.primary[100],
    },
    "&:active": {
      backgroundColor: colors.tertiary[200],
    },
    "&.Mui-disabled": {
      color: colors.tertiary[800],
      borderColor: colors.tertiary[300],
      opacity: 0.4,
    },
  },
  ghost: {
    backgroundColor: "transparent",
    color: colors.grey[800],
    border: `1px solid ${colors.grey[200]}`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
    "&:active": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
    "&.Mui-disabled": {
      color: colors.grey[500],
      borderColor: colors.grey[200],
      opacity: 0.4,
    },
  },
} as const;

const isGradient = (v: ButtonVariant) => v === "filled-gradient";

export const Button = ({
  variant = "filled-gradient",
  size = "large",
  endIcon,
  startIcon,
  animateIcon = true,
  fullWidth = false,
  loading = false,
  children,
  disabled,
  sx,
  ...props
}: ButtonProps) => {
  const config = sizeConfig[size];

  return (
    <MuiButton
      disabled={disabled || loading}
      fullWidth={fullWidth}
      sx={{
        fontFamily: fonts.inter,
        fontWeight: 600,
        fontSize: config.fontSize,
        lineHeight: "24px",
        textTransform: "none",
        height: config.height,
        padding: config.padding,
        borderRadius: config.borderRadius,
        minWidth: config.minWidth,
        gap: spacing[2],
        boxShadow: "none",
        whiteSpace: "nowrap",
        ...variantStyles[variant],
        ...(isGradient(variant) && {
          animation: `${btnBreathe} 3s ease-in-out infinite`,
        }),
        "&:hover": {
          ...variantStyles[variant]["&:hover"],
          boxShadow: "none",
          ...(animateIcon && {"& .btn-icon": {transform: "translateX(4px)"}}),
        },
        ...sx,
      }}
      {...props}
    >
      {loading && <CircularProgress size={20} sx={{color: "inherit"}} />}
      {!loading && startIcon}
      {!loading && children}
      {!loading && endIcon && (
        <span className="btn-icon" style={{display: "flex", transition: "transform 0.25s ease"}}>
          {endIcon}
        </span>
      )}
    </MuiButton>
  );
};
